import { Copy, Phone, MapPin, Clock, MessageCircle, Download, Globe } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { CONTACTS } from "./data";

const copyText = async (text, message) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(message);
  } catch {
    toast.error("Copia non riuscita");
  }
};

const downloadVCard = () => {
  const vcard = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    "FN:Furlan Print Copy",
    `EMAIL:${CONTACTS.email}`,
    `TEL;TYPE=WORK:${CONTACTS.phoneRaw}`,
    `TEL;TYPE=CELL:${CONTACTS.mobileRaw}`,
    "ADR:;;Viale dell'Industria 3;Paderno Dugnano;MI;20037;Italia",
    `URL:${CONTACTS.websiteUrl}`,
    "END:VCARD",
  ].join("\n");
  const url = URL.createObjectURL(new Blob([vcard], { type: "text/vcard" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "furlan-print-copy.vcf";
  a.click();
  URL.revokeObjectURL(url);
  toast.success("Biglietto da visita scaricato");
};

export const Contacts = () => (
  <section id="contatti" data-testid="contacts-section" className="py-24 sm:py-32 lg:py-40 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
    <Reveal>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#C85A32] mb-4">Contatti</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] text-[#181817] max-w-2xl">
        Nessun modulo. Chiamaci e basta.
      </h2>
      <p className="mt-6 text-lg text-[#6E6D69] font-light max-w-xl leading-relaxed">
        Preferiamo una telefonata: ecco tutti i canali diretti per raggiungerci subito.
      </p>
    </Reveal>
    <div className="mt-16 grid lg:grid-cols-2 gap-12 lg:gap-20">
      <Reveal delay={0.1} className="flex flex-col divide-y divide-[#E5E2DC] border-y border-[#E5E2DC]">
        <div className="py-7 flex items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">Telefono</p>
            <a
              data-testid="contact-phone-text"
              href={`tel:${CONTACTS.phoneRaw}`}
              className="mt-2 block font-display text-2xl sm:text-3xl font-light text-[#181817] hover:text-[#C85A32] transition-colors duration-300"
            >
              {CONTACTS.phone}
            </a>
          </div>
          <button
            data-testid="copy-phone-button"
            onClick={() => copyText(CONTACTS.phone, "Numero copiato negli appunti")}
            aria-label="Copia numero di telefono"
            className="w-11 h-11 shrink-0 rounded-full border border-[#C5C2BA] flex items-center justify-center text-[#181817] hover:bg-[#181817] hover:text-[#FBF9F5] transition-colors duration-300"
          >
            <Copy size={16} />
          </button>
        </div>
        <div className="py-7 flex items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">Cellulare</p>
            <a
              data-testid="contact-mobile-text"
              href={`tel:${CONTACTS.mobileRaw}`}
              className="mt-2 block font-display text-2xl sm:text-3xl font-light text-[#181817] hover:text-[#C85A32] transition-colors duration-300"
            >
              {CONTACTS.mobile}
            </a>
          </div>
          <button
            data-testid="copy-mobile-button"
            onClick={() => copyText(CONTACTS.mobile, "Numero copiato negli appunti")}
            aria-label="Copia numero di cellulare"
            className="w-11 h-11 shrink-0 rounded-full border border-[#C5C2BA] flex items-center justify-center text-[#181817] hover:bg-[#181817] hover:text-[#FBF9F5] transition-colors duration-300"
          >
            <Phone size={16} />
          </button>
        </div>
        <div className="py-7 flex items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">Email</p>
            <a
              data-testid="contact-email-text"
              href={`mailto:${CONTACTS.email}`}
              className="mt-2 block font-display text-2xl sm:text-3xl font-light text-[#181817] hover:text-[#C85A32] transition-colors duration-300 break-all"
            >
              {CONTACTS.email}
            </a>
          </div>
          <button
            data-testid="copy-email-button"
            onClick={() => copyText(CONTACTS.email, "Email copiata negli appunti")}
            aria-label="Copia email"
            className="w-11 h-11 shrink-0 rounded-full border border-[#C5C2BA] flex items-center justify-center text-[#181817] hover:bg-[#181817] hover:text-[#FBF9F5] transition-colors duration-300"
          >
            <Copy size={16} />
          </button>
        </div>
        <div className="py-7 flex items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">Sede</p>
            <p data-testid="contact-address-text" className="mt-2 font-display text-2xl sm:text-3xl font-light text-[#181817]">
              {CONTACTS.address}
            </p>
          </div>
          <a
            data-testid="directions-link"
            href={CONTACTS.mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Indicazioni stradali"
            className="w-11 h-11 shrink-0 rounded-full border border-[#C5C2BA] flex items-center justify-center text-[#181817] hover:bg-[#181817] hover:text-[#FBF9F5] transition-colors duration-300"
          >
            <MapPin size={16} />
          </a>
        </div>
        <div className="py-7 flex items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E6D69]">Sito Web</p>
            <a
              data-testid="contact-website-text"
              href={CONTACTS.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 block font-display text-2xl sm:text-3xl font-light text-[#181817] hover:text-[#C85A32] transition-colors duration-300"
            >
              {CONTACTS.website}
            </a>
          </div>
          <Globe size={18} className="text-[#C5C2BA] shrink-0" />
        </div>
        <div className="py-7 flex items-start gap-4">
          <Clock size={16} className="text-[#C85A32] shrink-0 mt-0.5" />
          <div>
            <p data-testid="contact-hours-text" className="text-sm text-[#6E6D69]">{CONTACTS.hours}</p>
            <p data-testid="contact-appointment-notice" className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#C85A32]">
              Si prega di prendere appuntamento prima di passare a trovarci
            </p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="bg-[#EBE6DD] rounded-sm overflow-hidden h-full flex flex-col">
          <img
            data-testid="contact-office-photo"
            src="/sede.jpg"
            alt="La sede Furlan Print Copy in Viale dell'Industria 3, Paderno Dugnano"
            className="w-full aspect-[3/2] object-cover"
          />
          <div className="p-8 sm:p-10 flex flex-col flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4A5446]">La nostra sede</p>
          <h3 className="mt-4 font-display text-3xl font-light text-[#181817] leading-snug">
            La risposta più veloce? Un messaggio WhatsApp.
          </h3>
          <p className="mt-4 text-sm text-[#181817]/70 leading-relaxed">
            Scrivici per un preventivo, un guasto o un riordino toner: ti rispondiamo in giornata. Oppure passa a trovarci in Viale dell'Industria 3 a Paderno Dugnano.
          </p>
          <div className="mt-auto pt-10 flex flex-wrap gap-4">
            <a
              data-testid="whatsapp-button"
              href={CONTACTS.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-full text-sm hover:bg-[#1eb856] transition-colors duration-300"
            >
              <MessageCircle size={15} /> Scrivici su WhatsApp
            </a>
            <button
              data-testid="vcard-download-button"
              onClick={downloadVCard}
              className="flex items-center gap-2 border border-[#181817] text-[#181817] px-6 py-3 rounded-full text-sm hover:bg-[#181817] hover:text-[#FBF9F5] transition-colors duration-300"
            >
              <Download size={15} /> Salva Contatto
            </button>
          </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
